import React, { ChangeEventHandler, useCallback, useEffect, useState } from 'react';

import { useSnackbar } from 'notistack';
import { Helmet } from 'react-helmet';
import { useParams } from 'react-router';

import { Button, Checkbox, Container, FormControlLabel, Stack, Typography } from '@mui/material';

import OfferBreadcrumbs from '~/components/Common/Elements/OfferBreadcrumbs';
import PageHeader from '~/components/Common/Elements/PageHeader';
import PageSubheader from '~/components/Common/Elements/PageSubheader';
import VimeoWidget from '~/components/Common/Forms/widgets/VimeoWidget';
import Spinner from '~/components/Common/Spinner';

import { OFFER_TYPE_HOTEL, OFFER_TYPE_LAST_MINUTE_HOTEL, OFFER_TYPE_TACTICAL_AO_HOTEL } from '~/consts/offerTypes';

import OffersService from '~/services/OffersService';

import featureToggle from '~/utils/featureToggle';
import { getHotelUniquePropertyDetails } from '~/utils/hotelUtils';
import { reportError } from '~/utils/reportError';

interface RouteParams {
  id_offer: string;
}

export default function VideosEditContainer() {
  const { id_offer: offerId } = useParams<RouteParams>();

  const [offerDetails, setOfferDetails] = useState(null);
  const [propertyDetails, setPropertyDetails] = useState<App.Property>(null);
  const [leVimeoId, setLeVimeoId] = useState<string>(null);
  const [whitelabelVimeoId, setWhitelabelVimeoId] = useState<string>(null);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
  const [isOverridableContent, setIsOverridableContent] = useState<boolean>(false);
  const [isVideoVimeoHero, setVideoVimeoHero] = useState(false);
  const [isWhiteLabelVideoVimeoHero, setWhiteLabelVideoVimeoHero] = useState(false);

  const { enqueueSnackbar } = useSnackbar();

  const fetchData = async () => {
    try {
      const { result } = await OffersService.getOffer(offerId);
      setOfferDetails(result);
      setPropertyDetails(await getHotelUniquePropertyDetails(result));
      setLeVimeoId(result.videos.luxuryescapes.vimeo_id);
      setVideoVimeoHero(result.videos.luxuryescapes.is_video_vimeo_hero);
      setWhiteLabelVideoVimeoHero(result.videos.whitelabel.is_video_vimeo_hero);
      setWhitelabelVimeoId(result.videos.whitelabel.vimeo_id);
      const overridableContent =
        featureToggle.availableToShow('ADDITIONAL_PROPERTY_CONTENT_ENABLED') &&
        [OFFER_TYPE_TACTICAL_AO_HOTEL, OFFER_TYPE_HOTEL, OFFER_TYPE_LAST_MINUTE_HOTEL].includes(result.type);
      setIsOverridableContent(overridableContent);
      setIsLoading(false);
    } catch (err) {
      reportError(err);
    }
  };

  const handleVideoVimeoHeroCheckboxChange = useCallback<ChangeEventHandler<HTMLInputElement>>((event) => {
    setVideoVimeoHero(event.target.checked);
  }, []);

  const handleWhiteLabelVideoVimeoHeroCheckboxChange = useCallback<ChangeEventHandler<HTMLInputElement>>((event) => {
    setWhiteLabelVideoVimeoHero(event.target.checked);
  }, []);

  const saveData = async () => {
    setIsSubmitting(true);

    try {
      const { result } = await OffersService.updateOffer(
        {
          type: offerDetails.type,
          video_vimeo_id: leVimeoId || null,
          whitelabel_video_vimeo_id: whitelabelVimeoId || null,
          is_video_vimeo_hero: isVideoVimeoHero,
          is_whitelabel_video_vimeo_hero: isWhiteLabelVideoVimeoHero,
        },
        offerId,
      );

      setOfferDetails(result);
      enqueueSnackbar(`Successfully saved videos`, { variant: 'success' });
    } catch (err) {
      reportError(err);
      enqueueSnackbar(`"Failed to save videos`, { variant: 'error' });
    } finally {
      setIsSubmitting(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, [offerId]);

  return (
    <Container maxWidth="xl">
      <Helmet>
        <title>Offers | {offerDetails?.name || offerId} | Edit Offer Videos</title>
      </Helmet>
      <OfferBreadcrumbs offerId={offerId} currentPage="Videos" />
      <PageHeader title="Edit Offer Videos" />
      {!isLoading && (
        <>
          <Typography variant="h5">Vimeo video (Luxury Escapes)</Typography>
          <VimeoWidget value={leVimeoId} onChange={setLeVimeoId} />
          <FormControlLabel
            control={<Checkbox checked={isVideoVimeoHero} onChange={handleVideoVimeoHeroCheckboxChange} />}
            label="Use video has hero image"
          />
          <Typography variant="h5">Vimeo video (Whitelabelled)</Typography>
          <VimeoWidget value={whitelabelVimeoId} onChange={setWhitelabelVimeoId} />
          <Stack direction="column" spacing={1}>
            <FormControlLabel
              control={
                <Checkbox
                  checked={isWhiteLabelVideoVimeoHero}
                  onChange={handleWhiteLabelVideoVimeoHeroCheckboxChange}
                />
              }
              label="Use video has hero image"
            />
            <Button sx={{ width: 100 }} onClick={saveData} variant="contained" disabled={isSubmitting}>
              {isSubmitting ? <Spinner size={20} /> : 'Save'}
            </Button>
          </Stack>
          {isOverridableContent && (
            <>
              <PageSubheader title="Property Videos" />
              {propertyDetails && (
                <>
                  <Typography variant="h5">Vimeo video (Luxury Escapes)</Typography>
                  {propertyDetails?.videos?.luxuryescapes?.vimeo_id && (
                    <>
                      <VimeoWidget
                        value={propertyDetails.videos.luxuryescapes.vimeo_id}
                        onChange={setLeVimeoId}
                        readOnly={true}
                      />
                      <Button onClick={() => setLeVimeoId(propertyDetails.videos.luxuryescapes.vimeo_id)}>Copy</Button>
                    </>
                  )}
                  {!propertyDetails?.videos?.luxuryescapes?.vimeo_id && (
                    <Typography> No property video found for Luxury Escapes</Typography>
                  )}

                  <Typography variant="h5">Vimeo video (Whitelabelled)</Typography>
                  {propertyDetails?.videos?.whitelabel?.vimeo_id && (
                    <>
                      <VimeoWidget
                        value={propertyDetails.videos.whitelabel.vimeo_id}
                        onChange={setLeVimeoId}
                        readOnly={true}
                      />
                      <Button onClick={() => setWhitelabelVimeoId(propertyDetails.videos.whitelabel.vimeo_id)}>
                        Copy
                      </Button>
                    </>
                  )}
                  {!propertyDetails?.videos?.whitelabel?.vimeo_id && (
                    <Typography> No property video found for Whitelabelled</Typography>
                  )}
                </>
              )}
              {!propertyDetails && <Typography> No property has been linked to the offer yet!</Typography>}
            </>
          )}
        </>
      )}
      {isLoading && <Spinner />}
    </Container>
  );
}
